import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { CustomEventService } from '@shared/services/custom-event.service';

import ActionTypesEnum from '@shared/enums/action-constant.enum';
import { OwnerEnum } from '@shared/enums/owner.enum';
import {
  createFormGroup,
  ownerObjectUpdater,
  uniqueNameValidator,
  updateFormValidators,
  validateFormGroup
} from '@shared/utils/form.util';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'app-add-portfolio-item-modal',
  templateUrl: './add-portfolio-item-modal.component.html'
})
export class AddPortfolioItemModalComponent implements OnInit, OnDestroy {

  @Input() loading: boolean;
  @Input() buttonLoading: boolean;
  @Input() mode: 'edit' | 'add';
  @Input() data;
  @Input() title: string;
  @Input() currentStepIndex = 0;
  @Input() addOns: Addon.AddonModel[];
  @Input() users: User.UserModel[];
  @Input() teams: Team.TeamModel[];
  @Input() items: ItemPortfolio.ItemModel[];
  @Input() components?: ComponentPortfolio.ComponentModel[];

  private _fields: Widget.Field[];

  @Input()
  set fields(fields: Widget.Field[]) {
    fields.forEach(field => {
      field.required = field.validators.find(item => item === 'required');
    });

    this._fields = fields;
    this.fieldsForm = createFormGroup(fields);
    setTimeout(() => {
      this.changeStep('ascend');
      this.showFields = true;
    }, 100);
  }

  get fields(): Widget.Field[] {
    return this._fields;
  }

  ownerEnum = OwnerEnum;

  // step 1 - add on select form
  addOnSelectForm: FormGroup;
  addOnCategories: Addon.Category[];
  codesOfAddOnCategories: string[];
  filteredAddOns: Addon.AddonModel[] = [];

  // step 2 - tool select form
  fieldsForm: FormGroup;
  showFields = false;
  selectedFields: Map<string, Widget.Field> = new Map();
  selectedScopeKey: string;

  // step 3 - item detail select form
  addItemForm: FormGroup;
  selectedUserAndTeams = {
    [this.ownerEnum.USER]: [],
    [this.ownerEnum.USER]: []
  };

  constructor(private fb: FormBuilder,
              private customEventService: CustomEventService) { }

  ngOnInit(): void {
    const { id, name, owners = [], components = [], fields } = this.data;

    if (this.mode === 'add') {
      this.createAddOnForm();
    } else {
      this.fields = fields;
    }

    if (owners && owners.length) {
      owners.forEach(item => {
        this.selectedUserAndTeams[item.type] = item.ids;
      });
    }

    this.addItemForm = this.fb.group({
      id: [id, []],
      name: [name, [Validators.required, uniqueNameValidator(this.items, this.data)]],
      users: [this.selectedUserAndTeams[this.ownerEnum.USER], []],
      teams: [this.selectedUserAndTeams[this.ownerEnum.TEAM], []],
      components: [components.map(item => item.id), []]
    });
  }

  createAddOnForm(): void {
    const categories = this.addOns.map(item => item.category);

    this.codesOfAddOnCategories = Array.from(new Set(categories.map(item => item.code)));
    this.addOnCategories = this.codesOfAddOnCategories.map(item => categories.find(category => category.code === item));

    this.addOnSelectForm = this.fb.group({
      category: [null, [Validators.required]],
      addOn: [null, [Validators.required]],
    });

    this.addOnSelectForm.get('category').valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(categoryKey => {
        this.filteredAddOns = this.addOns.filter(addOn => addOn.category.code === categoryKey);

        if (this.filteredAddOns.every(item => (item.id !== this.addOnSelectForm.get('addOn').value)
          && (item.scope && item.scope.key ? item.scope.key !== this.addOnSelectForm.get('addOn').value : true))) {
          this.addOnSelectForm.get('addOn').setValue(null);
        }

        if (this.filteredAddOns.length === 1) {
          this.addOnSelectForm.get('addOn').setValue(this.filteredAddOns[0].id);
        }
      });
  }

  changeCurrentStep($event: number): void {
    this.currentStepIndex = $event;
  }

  changeStep(type: 'ascend' | 'descend') {
    if (type === 'ascend' && this.currentStepIndex === 1) {

      validateFormGroup(this.fieldsForm);

      if (this.fieldsForm.invalid) {
        return;
      }
    }
    if (type === 'ascend' && this.currentStepIndex === 2) {
      this.createItem();
      return;
    }
    this.currentStepIndex = type === 'ascend' ? ++this.currentStepIndex : --this.currentStepIndex;
  }

  createItem(): void {
    validateFormGroup(this.addItemForm);

    if (this.addItemForm.invalid) {
      return;
    }

    const formData = ownerObjectUpdater({
      fields: this.fieldsForm.value,
      ...this.addItemForm.value
    });

    if (this.mode === 'add' && this.addOnSelectForm.value) {
      formData.addonId = this.addOnSelectForm.get('addOn').value;
      formData.scopeKey = this.selectedScopeKey;
    }

    this.customEventService.announceEvent(this.mode === 'add' ? ActionTypesEnum.CREATE_ITEM : ActionTypesEnum.UPDATE_ITEM, formData);
  }

  selectField(value, field): void {
    this.fieldsForm = updateFormValidators(this.fields, this.fieldsForm);
    this.selectedFields.set(field.key, value);

    this.customEventService.announceEvent(ActionTypesEnum.ITEM_FIELD_SELECTED, {field, form: this.fieldsForm, value, scopeKey: this.selectedScopeKey});
  }

  getItemTemplates(): void {
    validateFormGroup(this.addOnSelectForm);

    if (this.addOnSelectForm.invalid) {
      return;
    }

    const formData = this.addOnSelectForm.value;

    const selectedAddOn = this.addOns.find(item => item.scope && item.scope.key ? item.scope.key === formData.addOn : item.id === formData.addOn);

    if (selectedAddOn.scope) {
      this.selectedScopeKey = selectedAddOn.scope.key;
    }

    this.customEventService.announceEvent(ActionTypesEnum.GET_ITEM_TEMPLATES, {
      addOnId: selectedAddOn.id,
      scope: this.selectedScopeKey
    });
  }

  cancelForm(): void {
    this.customEventService.announceEvent(ActionTypesEnum.CLOSE_MODAL);
  }

  ngOnDestroy(): void {
    // prevent memory leak when component destroyed
  }

  searchSelect(value: string, field: any) {
    if(value.length > 0) {
      field.data.content = field.data.contentAll.filter(item => item.label.toLocaleLowerCase().includes(value.toLocaleLowerCase())).slice(0, 500);
    } else {
      field.data.content = field.data.contentAll ? field.data.contentAll.slice(0, 500) : field.data.content;
    }
  }
}
